/**
 * Verträge des Konzept-Graphen (AP3.T3.2).
 *
 * Der Konzept-Graph ist das Rückgrat des Lernpfads: AP4 führt Mastery und
 * Skill-Ratings je Themenbereich, AP5 unterrichtet entlang der Prerequisites.
 * Beides braucht dieselben Konstanten wie Backend und Review-Ansicht — deshalb
 * liegen sie hier und nirgendwo sonst.
 */
/* -------------------------------------------------------------------------
 * Themenbereiche — die Achsen des späteren Skill-Ratings (AP4/F09)
 * ---------------------------------------------------------------------- */
/**
 * Feste Liste der Themenbereiche. **Jedes Konzept hat genau einen.**
 *
 * Die Liste ist bewusst kurz und deckt das Buch vollständig ab. Sie ist nach
 * AP4 nur noch schwer zu ändern, weil dort Ratings je Bereich geführt werden —
 * Begründung und Zuschnitt siehe ADR-0031 in `docs/DECISIONS.md`.
 */
export const CONCEPT_TOPIC_AREAS = [
    { id: 'grundlagen-mathematik', label: 'Grundlagen und Mathematik' },
    { id: 'spieltheorie', label: 'Spieltheorie' },
    { id: 'software-werkzeuge', label: 'Software und Werkzeuge' },
    { id: 'preflop-ranges', label: 'Preflop-Ranges' },
    { id: 'preflop-verteidigung', label: 'Preflop-Verteidigung' },
    { id: 'spiel-gegen-3bets', label: 'Spiel gegen 3-Bets' },
    { id: 'turnier-metriken-icm', label: 'Turnier-Metriken und ICM' },
    { id: 'postflop-grundlagen', label: 'Postflop-Grundlagen' },
    { id: 'flop-spiel', label: 'Flop-Spiel' },
    { id: 'turn-spiel', label: 'Turn' },
    { id: 'river-spiel', label: 'River' },
    { id: 'mental-game', label: 'Mental Game' },
];
/** Nur die Kennungen, in der Reihenfolge der Liste. */
export const CONCEPT_TOPIC_AREA_IDS = CONCEPT_TOPIC_AREAS.map((area) => area.id);
export function isConceptTopicArea(value) {
    return typeof value === 'string' && CONCEPT_TOPIC_AREA_IDS.includes(value);
}
/** Beschriftung eines Themenbereichs; fällt auf die Kennung zurück. */
export function conceptTopicAreaLabel(id) {
    return CONCEPT_TOPIC_AREAS.find((area) => area.id === id)?.label ?? id;
}
/* -------------------------------------------------------------------------
 * Level, Zustand, Herkunft
 * ---------------------------------------------------------------------- */
/**
 * Ab welchem Niveau ein Konzept sinnvoll ist. Die Stufen entsprechen den
 * Level-Fassungen der Lehrer-Persona aus T2.4.
 */
export const CONCEPT_LEVELS = ['einsteiger', 'fortgeschritten', 'experte'];
export function isConceptLevel(value) {
    return typeof value === 'string' && CONCEPT_LEVELS.includes(value);
}
/** `draft` = KI-Vorschlag, noch nicht bestätigt. `approved` = menschlich geprüft. */
export const CONCEPT_STATES = ['draft', 'approved'];
export function isConceptState(value) {
    return typeof value === 'string' && CONCEPT_STATES.includes(value);
}
/** Woher das Konzept stammt. */
export const CONCEPT_ORIGINS = ['ai', 'manual'];
/* -------------------------------------------------------------------------
 * JSON-Schema der KI-Vorschläge
 * ---------------------------------------------------------------------- */
/**
 * Schema, gegen das das Template `task/concept-taxonomy` antwortet.
 *
 * Bewusst flach und ohne IDs: Das Modell kennt keine Datenbankschlüssel. Es
 * verweist auf Voraussetzungen und Sektionen über **Titel** bzw.
 * **Sektionsschlüssel**; die Auflösung auf echte IDs ist deterministischer
 * Code (`apps/backend/src/concept/resolve.ts`), keine Modellaufgabe.
 */
export const CONCEPT_SUGGESTION_SCHEMA = {
    type: 'object',
    properties: {
        konzepte: {
            type: 'array',
            items: {
                type: 'object',
                properties: {
                    titel: { type: 'string' },
                    kurzdefinition: { type: 'string' },
                    themenbereich: { type: 'string', enum: [...CONCEPT_TOPIC_AREA_IDS] },
                    ab_level: { type: 'string', enum: [...CONCEPT_LEVELS] },
                    voraussetzungen: { type: 'array', items: { type: 'string' } },
                    sektionen: { type: 'array', items: { type: 'string' } },
                },
                required: [
                    'titel',
                    'kurzdefinition',
                    'themenbereich',
                    'ab_level',
                    'voraussetzungen',
                    'sektionen',
                ],
                additionalProperties: false,
            },
        },
    },
    required: ['konzepte'],
    additionalProperties: false,
};
/** Art einer Auffälligkeit im Konzept-Graphen. */
export const CONCEPT_ISSUE_KINDS = [
    'unresolved-prerequisite',
    'cycle',
    'duplicate',
    'without-section',
    'chapter-empty',
];
export function isConceptErrorResponse(value) {
    return (typeof value === 'object' &&
        value !== null &&
        value.error === 'invalid_concept' &&
        Array.isArray(value.fields));
}
//# sourceMappingURL=concept.js.map